import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getNoticiaPublic } from "services/publicNoticiasService";
import { getConfig } from "config/config";
import DOMPurify from "dompurify";
import { usePageLoading } from "hooks/usePageLoading";

// Los links dentro del texto de la noticia abren en una pestaña nueva
// (navegador del sistema si es PWA). rel="noopener" por seguridad.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// Detalle PÚBLICO de una noticia: card con título, foto y texto completo,
// más un botón para volver al listado. Solo lectura.

const config = getConfig();
const imagenUrl = (filename) => (filename ? config.filesUrl + "/noticias/" + filename : "");

const formatFecha = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" });
};

export default function NoticiaDetalle(props) {
  const doneLoading = usePageLoading(props);
  const { id } = useParams();
  const navigate = useNavigate();
  const [noticia, setNoticia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    (async () => {
      try {
        const rec = await getNoticiaPublic(id);
        if (rec && rec._id) setNoticia(rec);
        else setError(true);
      } finally {
        setLoading(false);
        doneLoading();
      }
    })();
  }, [id]);

  const volver = () => {
    // Si venimos del listado, back mantiene el scroll; si no, vamos al listado.
    if (window.history.length > 1) navigate(-1);
    else navigate("/noticias");
  };

  // Los links del texto deben abrirse en una instancia NUEVA del navegador del
  // sistema, no dentro de la ventana de la PWA. Un <a target="_blank"> en modo
  // standalone suele abrir sobre la propia PWA; forzamos un contexto de
  // navegación nuevo con window.open, que sí deriva al navegador por defecto.
  const handleContentClick = (e) => {
    const anchor = e.target.closest("a");
    if (anchor && anchor.href) {
      e.preventDefault();
      window.open(anchor.href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="npub">
      <div className="tpub-shell">
        <div className="tpub-head">
          <button type="button" className="tpub-back" onClick={volver} aria-label="Volver">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="tpub-head-text">
            <div className="tpub-title">Noticia</div>
          </div>
        </div>

        {loading ? (
          <p className="tpub-loading">Cargando…</p>
        ) : error || !noticia ? (
          <p className="tpub-empty">No se encontró la noticia.</p>
        ) : (
          <div className="npub-card npub-card--detalle">
            {imagenUrl(noticia.imagen) && (
              <div className="npub-detalle-foto">
                <img src={imagenUrl(noticia.imagen)} alt={noticia.titulo} />
              </div>
            )}
            <div className="npub-card-body">
              <div className="npub-fecha">{formatFecha(noticia.fecha)}</div>
              <div className="npub-detalle-title">{noticia.titulo}</div>
              <div
                className="npub-detalle-texto"
                onClick={handleContentClick}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(noticia.contenido || "") }}
              />
              <button type="button" className="npub-volver" onClick={volver}>
                Volver
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
